import { Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import SiteLayout from "./components/SiteLayout";
import ConsentBanner from "./components/ConsentBanner";
import HomePage from "./pages/HomePage";
import StartHerePage from "./pages/StartHerePage";
import { CATEGORIES } from "./lib/categories";
import { BOOKS } from "./lib/books";

const AboutPage = lazy(() => import("./pages/AboutPage"));
const SuicideAndCrisisPage = lazy(() => import("./pages/SuicideAndCrisisPage"));
const SafetyPlanPage = lazy(() => import("./pages/SafetyPlanPage"));
const ContributorsIndexPage = lazy(() => import("./pages/ContributorsIndexPage"));
const MedicalReviewBoardPage = lazy(() => import("./pages/MedicalReviewBoardPage"));
const ShariqRefaiPage = lazy(() => import("./pages/ShariqRefaiPage"));
const EditorialStandardsPage = lazy(() => import("./pages/EditorialStandardsPage"));
const EditorialStrategyPage = lazy(() => import("./pages/EditorialStrategyPage"));
const EditorialProcessPage = lazy(() => import("./pages/EditorialProcessPage"));
const MedicalDisclaimerPage = lazy(() => import("./pages/MedicalDisclaimerPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsOfUsePage = lazy(() => import("./pages/TermsOfUsePage"));
const CookiePolicyPage = lazy(() => import("./pages/CookiePolicyPage"));
const AccessibilityPage = lazy(() => import("./pages/AccessibilityPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const VerifiedPage = lazy(() => import("./pages/VerifiedPage"));
const MediaPage = lazy(() => import("./pages/MediaPage"));
const GlossaryIndexPage = lazy(() => import("./pages/GlossaryIndexPage"));
const GlossaryEntryPage = lazy(() => import("./pages/GlossaryEntryPage"));
const CategoryIndexPage = lazy(() => import("./pages/CategoryIndexPage"));
const ConditionsIndexPage = lazy(() => import("./pages/ConditionsIndexPage"));
const ArticlePage = lazy(() => import("./pages/ArticlePage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const ResourcesIndexPage = lazy(() => import("./pages/ResourcesIndexPage"));
const LibraryPage = lazy(() => import("./pages/LibraryPage"));
const BooksIndexPage = lazy(() => import("./pages/BooksIndexPage"));
const BookDetailPage = lazy(() => import("./pages/BookDetailPage"));
const StateResourcesIndexPage = lazy(() => import("./pages/StateResourcesIndexPage"));
const StateResourcePage = lazy(() => import("./pages/StateResourcePage"));
const HowToFindTherapistPage = lazy(() => import("./pages/HowToFindTherapistPage"));
const JustDiagnosedPage = lazy(() => import("./pages/JustDiagnosedPage"));
const SupportingPartnerPage = lazy(() => import("./pages/SupportingPartnerPage"));
const AnxietyKeepingYouUpAtNightPage = lazy(
  () => import("./pages/AnxietyKeepingYouUpAtNightPage"),
);
const AnxietyFaqPage = lazy(() => import("./pages/AnxietyFaqPage"));
const MapsHubPage = lazy(() => import("./pages/MapsHubPage"));
const MapPage = lazy(() => import("./pages/MapPage"));
const AiUsePage = lazy(() => import("./pages/AiUsePage"));
const DisclosuresPage = lazy(() => import("./pages/DisclosuresPage"));
const CorrectionsPage = lazy(() => import("./pages/CorrectionsPage"));
const EvidenceMethodologyPage = lazy(() => import("./pages/EvidenceMethodologyPage"));
const InternationalNoticePage = lazy(() => import("./pages/InternationalNoticePage"));
const UserContentPage = lazy(() => import("./pages/UserContentPage"));
const ToolsIndexPage = lazy(() => import("./pages/ToolsIndexPage"));
const ToolPage = lazy(() => import("./pages/ToolPage"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function App() {
  return (
    <SiteLayout>
      <ScrollToTop />
      <ConsentBanner />
      <Suspense fallback={<div style={{ minHeight: "60vh" }} />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/start-here" element={<StartHerePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route
          path="/suicide-and-crisis"
          element={<SuicideAndCrisisPage />}
        />
        <Route path="/safety-plan" element={<SafetyPlanPage />} />
        <Route path="/contributors" element={<ContributorsIndexPage />} />
        <Route path="/medical-review-board" element={<MedicalReviewBoardPage />} />
        <Route path="/contributors/shariq-refai" element={<ShariqRefaiPage />} />
        <Route path="/editorial-standards" element={<EditorialStandardsPage />} />
        <Route path="/editorial-strategy" element={<EditorialStrategyPage />} />
        <Route path="/editorial-process" element={<EditorialProcessPage />} />
        <Route path="/medical-disclaimer" element={<MedicalDisclaimerPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms-of-use" element={<TermsOfUsePage />} />
        <Route path="/cookie-policy" element={<CookiePolicyPage />} />
        <Route path="/accessibility" element={<AccessibilityPage />} />
        <Route path="/ai-use" element={<AiUsePage />} />
        <Route path="/disclosures" element={<DisclosuresPage />} />
        <Route path="/corrections" element={<CorrectionsPage />} />
        <Route path="/evidence-methodology" element={<EvidenceMethodologyPage />} />
        <Route path="/international" element={<InternationalNoticePage />} />
        <Route path="/user-content" element={<UserContentPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/verified" element={<VerifiedPage />} />
        <Route path="/media" element={<MediaPage />} />
        <Route path="/anxiety-faq" element={<AnxietyFaqPage />} />
        <Route path="/maps" element={<MapsHubPage />} />
        <Route path="/maps/:slug" element={<MapPage />} />
        <Route path="/glossary" element={<GlossaryIndexPage />} />
        <Route path="/glossary/:term" element={<GlossaryEntryPage />} />
        <Route path="/tools" element={<ToolsIndexPage />} />
        <Route path="/tools/:slug" element={<ToolPage />} />
        <Route path="/resources" element={<ResourcesIndexPage />} />
        <Route path="/library" element={<LibraryPage />} />
        <Route
          path="/resources/how-to-find-a-therapist"
          element={<HowToFindTherapistPage />}
        />
        <Route
          path="/resources/just-diagnosed-with-anxiety"
          element={<JustDiagnosedPage />}
        />
        <Route
          path="/resources/supporting-a-partner-with-anxiety"
          element={<SupportingPartnerPage />}
        />
        <Route
          path="/resources/anxiety-keeping-you-up-at-night"
          element={<AnxietyKeepingYouUpAtNightPage />}
        />
        <Route path="/resources/books" element={<BooksIndexPage />} />
        <Route path="/resources/state-resources" element={<StateResourcesIndexPage />} />
        <Route path="/resources/state-resources/:state" element={<StateResourcePage />} />
        {BOOKS.map((b) => (
          <Route
            key={b.slug}
            path={b.internalPath}
            element={<BookDetailPage book={b} />}
          />
        ))}
        <Route path="/conditions" element={<ConditionsIndexPage />} />
        {CATEGORIES.filter((c) => c.slug !== "conditions").map((cat) => (
          <Route
            key={cat.slug}
            path={`/${cat.slug}`}
            element={<CategoryIndexPage category={cat} />}
          />
        ))}
        {CATEGORIES.map((cat) => (
          <Route
            key={`${cat.slug}-article`}
            path={`/${cat.slug}/:slug`}
            element={<ArticlePage category={cat} />}
          />
        ))}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
    </SiteLayout>
  );
}
